import React, { useState } from "react";
import { addMonths, subMonths } from "date-fns";
import { AnimatePresence } from "framer-motion";
import { Icon } from "../../../components/icons";
import { useGetTableData2 } from "../Plan/tableHooks";
import DayScheduleModal from "./DayScheduleModal";
import {
  generateCalendar,
  getPresetName,
  getDateData,
  getMonthRange,
} from "./utils";
import * as S from "./styles";
import { DAYS, days } from "./constants";
import SidebarContent from "./SidebarContent";
import { dayTypeMappingForSide } from "./constants";
import goToHelpLink from "../../../utils/goToHelpLink";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Tooltip } from "@mui/material";

const formatSelectedDate = (date) => {
  if (!date) return "";
  const dayOfWeek = days[date.getDay()];
  return `${date.getFullYear()}년 ${
    date.getMonth() + 1
  }월 ${date.getDate()}일 (${dayOfWeek})`;
};

function Management({ setCurrentTab, openSnackbar, group_code, project_code }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [isDateEditorOpen, setIsDateEditorOpen] = useState(false);
  const { presets } = useGetTableData2(
    true,
    `/api/factory/getTypeM?group_code=${group_code}&project_code=${project_code}`,
    "presets"
  );
  const caelndarObj = {
    date: getMonthRange(currentDate),
    group_code,
    project_code,
  };
  const { calendarData } = useGetTableData2(
    true,
    `/getInsightwinCalendarViewData?months=${JSON.stringify(caelndarObj)}`,
    "calendarData"
  );
  const { operDayTime } = useGetTableData2(
    true,
    `/api/factory/getDayM?group_code=${group_code}&project_code=${project_code}`,
    "operDayTime"
  );
  const handleDateClick = (date) => {
    if (isSelectedDate(date)) {
      setSelectedDate(null);
      setIsSidebarOpen(false);
    } else {
      setSelectedDate(date);
      setIsSidebarOpen(true);
    }
  };
  const handleMachineClick = () => {
    setCurrentTab(2);
    setIsSidebarOpen(false);
    setIsDateEditorOpen(false);
  };
  const handleDaySettingClick = () => {
    setCurrentTab(3);
    setIsSidebarOpen(false);
    setIsDateEditorOpen(false);
  };
  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };
  const isSelectedDate = (date) => {
    if (!selectedDate) return false;
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };
  const isGrayedDate = (date) => {
    return date.getMonth() !== currentDate.getMonth();
  };
  const handlePrevMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };
  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };
  const calendar = generateCalendar(currentDate);
  return (
    <S.CalendarPageWrapper>
      <S.CalendarWrapper $isSidebarOpen={isSidebarOpen}>
        <S.CalendarContainer>
          <S.CalendarHeader>
            <S.HeaderButton onClick={handlePrevMonth}>&lt;</S.HeaderButton>
            <div
              //   style={{ fontFamily: "GangwonEduPowerExtraBoldA" }}
              style={{ fontWeight: "700", fontSize: "22px", color: "#111" }}
            >
              {`${currentDate.getFullYear()}년 ${currentDate.getMonth() + 1}월`}
              &nbsp;{" "}
              <Tooltip
                title="이동"
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  goToHelpLink(
                    "https://help.ese-tech.com/ko/planner#h-9-2-%EA%B4%80%EB%A6%AC"
                  )
                }
              >
                <HelpOutlineIcon />
              </Tooltip>
            </div>
            <S.HeaderButton onClick={handleNextMonth}>&gt;</S.HeaderButton>
          </S.CalendarHeader>
          <S.DaysHeader>
            {DAYS.map((day) => (
              <div
                style={{ fontFamily: "rec_font", color: "#a8a8a8" }}
                key={day}
              >
                {day}
              </div>
            ))}
          </S.DaysHeader>
          <S.DatesGrid>
            {calendar.map((date, index) => {
              const { machineData, specData } = getDateData(date, calendarData);
              const isSelected = isSelectedDate(date);
              const isGrayed = isGrayedDate(date);

              // 요일별 프리셋 정보 구하기
              const selectedDayType = date.getDay();
              const dateString = `${date.getFullYear()}-${String(
                date.getMonth() + 1
              ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
              const currentDaySchedule = operDayTime?.find((schedule) => {
                const validFrom = new Date(schedule.valid_from)
                  .toISOString()
                  .split("T")[0];
                const validTo = new Date(schedule.valid_to)
                  .toISOString()
                  .split("T")[0];
                return dateString >= validFrom && dateString <= validTo;
              });
              const dayTypeId =
                currentDaySchedule?.[dayTypeMappingForSide[selectedDayType]];
              const dayPreset = presets?.find((p) => p.id === dayTypeId);
              return (
                <S.DateCell
                  key={index}
                  className={`${isGrayed ? "grayed" : ""} ${
                    isSelected ? "selected" : ""
                  } ${date.getDay() === 0 ? "sunday" : ""} ${
                    date.getDay() === 6 ? "saturday" : ""
                  }`}
                  onClick={() => handleDateClick(date)}
                >
                  <S.DateCellContent>
                    <S.DateHeader>
                      {machineData.length > 0 && (
                        <Icon
                          name="calendarIcon"
                          style={{ width: "16px", height: "16px" }}
                        />
                      )}
                      <S.DateNumber className="date-number">
                        {date.getDate()}
                      </S.DateNumber>
                    </S.DateHeader>
                    <S.DataContainer>
                      {machineData.length > 0 && (
                        <S.MachineTimeBox>
                          <strong>{`${machineData.length}`}</strong> 장비의
                          일정이 존재합니다
                        </S.MachineTimeBox>
                      )}
                      {specData.length > 0 ? (
                        // spec 데이터가 있는 경우
                        <S.SpecTimeBox $isHoliday={specData[0].type_id === -1}>
                          {specData[0].type_id === -1
                            ? specData[0].description || "휴일"
                            : getPresetName(specData[0].type_id, presets)}
                        </S.SpecTimeBox>
                      ) : (
                        // spec 데이터가 없는 경우 요일별 프리셋 확인
                        <S.SpecTimeBox
                          $isHoliday={dayTypeId === -1 || dayTypeId === 0}
                        >
                          {dayTypeId === -1
                            ? "휴일"
                            : dayTypeId === 0
                            ? "프리셋 없음"
                            : dayPreset?.name || "프리셋 없음"}
                        </S.SpecTimeBox>
                      )}
                    </S.DataContainer>
                  </S.DateCellContent>
                </S.DateCell>
              );
            })}
            {/* {calendar.map((date, index) => {
              const { machineData, specData } = getDateData(date, calendarData);
              const isSelected = isSelectedDate(date);
              const isGrayed = isGrayedDate(date);
              return (
                <S.DateCell
                  key={index}
                  className={`${isGrayed ? "grayed" : ""} ${
                    isSelected ? "selected" : ""
                  } ${date.getDay() === 0 ? "sunday" : ""} ${
                    date.getDay() === 6 ? "saturday" : ""
                  }`}
                  onClick={() => handleDateClick(date)}
                >
                  <S.DateCellContent>
                    <S.DateHeader>
                      <S.ScheduleCount $isSidebarOpen={isSidebarOpen}>
                        {machineData.length + specData.length > 3 && (
                          <>
                            <CalendarIcon size={16} />
                            <span className="tooltip">
                              총{" "}
                              <strong>
                                {machineData.length + specData.length}
                              </strong>
                              개의 일정
                            </span>
                          </>
                        )}
                      </S.ScheduleCount>
                      <S.DateNumber className="date-number">
                        {date.getDate()}
                      </S.DateNumber>
                    </S.DateHeader>
                    <S.DataContainer>
                      {[...machineData, ...specData]
                        .slice(0, 3)
                        .map((data, idx) => {
                          if ("machine_name" in data) {
                            return (
                              <S.MachineTimeBox
                                key={`machine-${idx}`}
                                title={`${data.machine_name} - ${getPresetName(
                                  data.type_id,
                                  presets
                                )}`}
                              >
                                {`${data.machine_name} - ${getPresetName(
                                  data.type_id,
                                  presets
                                )}`}
                              </S.MachineTimeBox>
                            );
                          } else {
                            return (
                              <S.SpecTimeBox
                                key={`spec-${idx}`}
                                $isHoliday={data.type_id === -1}
                              >
                                {data.type_id === -1
                                  ? "휴일"
                                  : getPresetName(data.type_id, presets)}
                              </S.SpecTimeBox>
                            );
                          }
                        })}
                    </S.DataContainer>
                  </S.DateCellContent>
                </S.DateCell>
              );
            })} */}
          </S.DatesGrid>
        </S.CalendarContainer>
      </S.CalendarWrapper>
      <AnimatePresence>
        {isSidebarOpen && (
          <S.Sidebar
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <S.HeaderContainer>
              <S.SelectedDateText>
                {formatSelectedDate(selectedDate)}
              </S.SelectedDateText>
              <S.CloseButton onClick={closeSidebar}>
                <Icon
                  name="closeIcon"
                  style={{ width: "20px", height: "20px" }}
                />
              </S.CloseButton>
            </S.HeaderContainer>
            <S.SectionsWrapper>
              <SidebarContent
                selectedDate={selectedDate}
                calendarData={calendarData}
                presets={presets}
                isDateEditorOpen={isDateEditorOpen}
                setIsDateEditorOpen={setIsDateEditorOpen}
                handleMachineClick={handleMachineClick}
                setDayModalOpen={setDayModalOpen}
                handleDaySettingClick={handleDaySettingClick}
                openSnackbar={openSnackbar}
                group_code={group_code}
                project_code={project_code}
              />
            </S.SectionsWrapper>
          </S.Sidebar>
        )}
      </AnimatePresence>
      <DayScheduleModal
        open={dayModalOpen}
        onClose={() => setDayModalOpen(false)}
        selectedDate={selectedDate}
      />
    </S.CalendarPageWrapper>
  );
}

export default Management;
